import type { PillTone } from "@/components/ui/pill";
import type {
  AdherenceDimensionKey,
  AdherenceVerdict,
  AppliedScenarioPhase,
} from "@/types/applied-scenario.types";

// Vocabulário da tela do compromisso. Um lugar só: o mesmo rótulo aparece na lista, no
// detalhe e no cabeçalho, e três traduções paralelas divergem no primeiro ajuste de copy.
// Status NUNCA só por cor (design system): cada fase carrega cor + dot + rótulo, e o `Pill`
// com `withDot` é o componente que garante os três juntos.

export const PHASE_LABELS: Record<AppliedScenarioPhase, string> = {
  scheduled: "Agendado",
  running: "Em vigência",
  awaiting_settlement: "Aguardando apuração",
  settleable: "Pronto para apurar",
  settled: "Apurado",
  superseded: "Substituído",
  cancelled: "Cancelado",
};

export const PHASE_TONES: Record<AppliedScenarioPhase, PillTone> = {
  scheduled: "info",
  running: "brand",
  awaiting_settlement: "warning",
  settleable: "success",
  settled: "neutral",
  superseded: "neutral",
  cancelled: "neutral",
};

/** `wall_clock` é uma LIMITAÇÃO a declarar, não um detalhe de implementação a esconder. */
export const WINDOW_BASIS_LABELS = {
  calendar: "Fim derivado do expediente da linha",
  wall_clock: "Linha sem expediente declarado — fim em tempo de parede",
} as const;

// ── Vocabulário da ADERÊNCIA ao compromisso ───────────────────────────────────────────────────
// ⚠️ "Aderência" aqui NÃO é o `planAdherence` do OEE, que compara a produção contra o
// ritmo do plano de UMA run. Esta compara a execução contra o COMPROMISSO congelado, e é ela que
// gateia a estatística de acerto do gêmeo. Dois conceitos de nome parecido; confundi-los faz o
// gestor ler o número errado.

export const VERDICT_LABELS: Record<AdherenceVerdict, string> = {
  ADHERENT: "Seguindo o combinado",
  AT_RISK: "Fora do combinado",
  BREACHED: "Não seguiu o combinado",
  NOT_MEASURED: "Não medida",
};

export const VERDICT_TONES: Record<AdherenceVerdict, PillTone> = {
  ADHERENT: "success",
  // `AT_RISK` é AVISO, não veredito final: a vigência ainda corre e dá para corrigir.
  AT_RISK: "warning",
  BREACHED: "danger",
  // Cinza de propósito: "não medida" não é bom nem ruim, é falta de sinal — pintá-la de vermelho
  // faria a instrumentação ausente parecer culpa da operação.
  NOT_MEASURED: "neutral",
};

/** O que cada veredito pede de quem lê. Um estado sem próximo passo é um estado inútil. */
export const VERDICT_HINTS: Record<AdherenceVerdict, string> = {
  ADHERENT: "A execução está dentro do limiar declarado ao aplicar.",
  AT_RISK: "Ainda dá para corrigir: a vigência não terminou.",
  BREACHED:
    "A vigência encerrou fora do limiar — este compromisso fica fora da estatística de acerto.",
  NOT_MEASURED:
    "Nenhuma dimensão pôde ser medida. Isto pede instrumentação, não conversa com a operação.",
};

export const DIMENSION_LABELS: Record<AdherenceDimensionKey, string> = {
  sequence: "Sequência",
  mix: "Mix",
  shifts: "Turnos",
  machines: "Máquinas",
};

/** O que cada dimensão mede, em uma linha — o suficiente para o número não precisar de manual. */
export const DIMENSION_HINTS: Record<AdherenceDimensionKey, string> = {
  sequence: "As ordens começaram na ordem comprometida.",
  mix: "A produção se distribuiu entre as ordens como foi prometido.",
  shifts: "O expediente executado é o expediente comprometido.",
  machines: "A linha operou com o arranjo de máquinas combinado. Quebra não conta.",
};
